import { App } from "@capacitor/app";
import { Cubit } from "blac";
import type { AvailableResult } from "@capgo/capacitor-native-biometric";
import {
  BiometricAuthError,
  BiometryType,
  NativeBiometric
} from "@capgo/capacitor-native-biometric";
import { globalEvents } from "src/constants/globalEvents";
import { isReallyNative } from "src/hybrid/components/Platform";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { TrackEvent } from "src/state/Track/TrackCubit";
import { toast, tracker } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import { FeatureFlagName, featureFlags } from "src/lib/featureFlags";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { onAppActiveChange } from "src/hybrid/appLifecycle";

/** Background time after which the app locks again, a common default for banking and health apps. */
export const RELOCK_AFTER_BACKGROUND_MS = 5 * 60 * 1000;

export interface BiometricVerificationState {
  isBiometricVerificationEnabled: boolean;
  isBiometricVerificationAvailable: boolean;
  showSetupPrompt: boolean;
  biometricType: BiometryType;
  isSessionVerified: boolean;
  requestedEmailLogin: boolean;
}

const isBiometricCancel = (error: unknown): boolean =>
  [
    BiometricAuthError.USER_CANCEL,
    BiometricAuthError.SYSTEM_CANCEL,
    BiometricAuthError.APP_CANCEL
  ].some((code) => String(code) === String((error as { code?: unknown }).code));

export class BiometricVerificationBloc extends Cubit<BiometricVerificationState> {
  server: string | null = null;
  // Gates real Capacitor plugin calls. Uses `isReallyNative()` (raw
  // Capacitor check) rather than `isHybridApp()` so the debug platform
  // override doesn't drive us into calling plugins that aren't implemented
  // on web. UI mocks still flow through bloc state via DebugBiometric.
  availableOnDevice = isReallyNative();

  log = (...args: unknown[]): void => {
    if (featureFlags.getFlag(FeatureFlagName.loggingBiometricsVerification)) {
      // eslint-disable-next-line no-console
      console.warn("BioVeri", ...args);
    }
    logSentryBreadcrumb("biometricsVerification", ...args);
  };

  promptText: Record<
    BiometryType,
    {
      reason?: TranslationKey;
      title?: TranslationKey;
      description?: TranslationKey;
    }
  > = {
    [BiometryType.FACE_ID]: {
      title: "signIn.with.faceId.title",
      reason: "signIn.with.faceId.title",
      description: "signIn.with.faceId.description"
    },
    [BiometryType.TOUCH_ID]: {
      title: "signIn.with.touchId.title",
      reason: "signIn.with.touchId.title",
      description: "signIn.with.touchId.description"
    },
    [BiometryType.FINGERPRINT]: {},
    [BiometryType.FACE_AUTHENTICATION]: {},
    [BiometryType.IRIS_AUTHENTICATION]: {},
    [BiometryType.MULTIPLE]: {},
    [BiometryType.DEVICE_CREDENTIAL]: {},
    [BiometryType.NONE]: {}
  };

  get biometricTypeName(): string | undefined {
    const type = this.state.biometricType;
    switch (type) {
      case BiometryType.FACE_ID:
        return "faceId";
      case BiometryType.TOUCH_ID:
        return "touchId";
      case BiometryType.FINGERPRINT:
        return "fingerprint";
      case BiometryType.FACE_AUTHENTICATION:
        return "faceAuthentication";
      case BiometryType.IRIS_AUTHENTICATION:
        return "iris";
      case BiometryType.MULTIPLE:
        return undefined;
      default:
        return undefined;
    }
  }

  constructor() {
    super({
      isBiometricVerificationEnabled: false,
      isBiometricVerificationAvailable: false,
      showSetupPrompt: false,
      biometricType: BiometryType.NONE,
      isSessionVerified: false,
      requestedEmailLogin: false
    });

    this.init().catch(reportErrorSentry);
    window.addEventListener(globalEvents.USER_CLEAR, () => {
      void this.disable();
    });
    onAppActiveChange(this.handleAppActiveChange);
  }

  /**
   * Verification is kept in memory only, so a cold start always locks: iOS can
   * keep `sessionStorage` across an app kill. A resume only locks after a long
   * enough background. The prompt itself makes the app inactive, so changes
   * during it are ignored.
   */
  readonly handleAppActiveChange = (isActive: boolean): void => {
    if (!this.availableOnDevice || this.isVerifying) {
      return;
    }

    if (!isActive) {
      this.backgroundedAt = Date.now();
      return;
    }

    const { backgroundedAt } = this;
    this.backgroundedAt = undefined;
    if (
      backgroundedAt !== undefined &&
      Date.now() - backgroundedAt >= RELOCK_AFTER_BACKGROUND_MS
    ) {
      this.log("relock after background");
      this.resetSessionVerified();
    }
  };

  async init(): Promise<void> {
    if (!this.availableOnDevice) {
      return;
    }
    await this.getServer();
    await this.checkSupport();
  }

  async getServer(): Promise<string | null> {
    if (!this.availableOnDevice) {
      return null;
    }
    const info = await App.getInfo();
    return info.id;
  }

  async isAvailable(): Promise<AvailableResult> {
    const result = await NativeBiometric.isAvailable();

    this.emit({
      ...this.state,
      isBiometricVerificationAvailable: result.isAvailable
    });

    return result;
  }

  checkSupport = async (): Promise<boolean> => {
    if (!this.availableOnDevice) {
      // On web — including debug-simulated hybrid — don't overwrite state.
      // The constructor seeds availability to false; the debug panel may
      // mock it to true. Either way, callers should see the current value
      // rather than a forced reset.
      return this.state.isBiometricVerificationAvailable;
    }
    const server = await this.getServer();
    if (!server) {
      reportErrorSentry(new Error("Credential server is not set"));
      return false;
    }

    const result = await this.isAvailable();

    this.log("isavailable", result);
    if (result.isAvailable) {
      const cachedEnabled = localStorage.getItem("biometricEnabled") === "true";
      this.emit({
        ...this.state,
        isBiometricVerificationAvailable: result.isAvailable,
        isBiometricVerificationEnabled: cachedEnabled,
        biometricType: result.biometryType
      });
      this.log("state", this.state);
    }

    return result.isAvailable;
  };

  sessionVerified = false;
  isVerifying = false;
  backgroundedAt: number | undefined;

  /**
   * Shows biometric prompt to verify the user's identity.
   * This is used for unlocking the app, not for credential storage.
   */
  readonly verifyIdentity = async (force = false): Promise<boolean> => {
    if (!this.availableOnDevice || this.isVerifying) {
      return false;
    }

    const supported = await this.checkSupport();
    if (!supported) {
      return false;
    }

    if (this.sessionVerified && !force) {
      return true;
    }

    this.isVerifying = true;
    let error: Error | undefined;

    tracker.track(TrackEvent.BioVerifyIdentity);

    const usePrompt = this.promptText[this.state.biometricType];

    const { reason, title, description } = usePrompt;

    BlockingLoadingOverlayController.startLoading({
      bg: "biometric",
      fadeIn: false
    });

    try {
      await NativeBiometric.verifyIdentity({
        maxAttempts: 5,
        reason: reason ? translate(reason) : translate("signIn.biometrics"),
        title: title ? translate(title) : translate("signIn.biometrics"),
        description: description
          ? translate(description)
          : translate("biometric.description")
      });

      this.sessionVerified = true;
      this.emit({
        ...this.state,
        isSessionVerified: true
      });
    } catch (e: unknown) {
      error = e as Error;
    }

    BlockingLoadingOverlayController.endLoading();
    this.isVerifying = false;

    if (error) {
      throw error;
    }
    return true;
  };

  resetSessionVerified = (): void => {
    this.sessionVerified = false;
    this.emit({
      ...this.state,
      isSessionVerified: false
    });
  };

  /**
   * Enable biometric app lock.
   * This only enables the lock feature - tokens are always stored in localStorage.
   */
  readonly enableBiometric = async (): Promise<void> => {
    if (!this.availableOnDevice) {
      return;
    }

    const supported = await this.checkSupport();
    if (!supported) {
      return;
    }

    try {
      if (!(await this.verifyIdentity(true))) {
        return;
      }

      this.emit({
        ...this.state,
        isBiometricVerificationEnabled: true,
        showSetupPrompt: false
      });
      localStorage.setItem("biometricEnabled", "true");

      toast.show("biometric.enabled");
    } catch (error) {
      if (!isBiometricCancel(error)) {
        reportErrorSentry(error);
      }
    }
  };

  /**
   * Disable biometric app lock.
   * Requires biometric verification before allowing disable.
   */
  readonly disableBiometric = async (): Promise<void> => {
    if (!this.availableOnDevice) {
      return;
    }

    try {
      if (!(await this.verifyIdentity(true))) {
        return;
      }
      this.emit({
        ...this.state,
        isBiometricVerificationEnabled: false
      });
      localStorage.removeItem("biometricEnabled");

      toast.show("biometric.disabled");
    } catch {
      this.log("Biometric verification failed - cannot disable biometrics");
      return;
    }
  };

  readonly openSetupPrompt = async (): Promise<void> => {
    if (!this.availableOnDevice) {
      return;
    }
    if (this.state.isBiometricVerificationEnabled) {
      return;
    }

    this.emit({
      ...this.state,
      showSetupPrompt: true
    });
  };

  readonly closeSetupPrompt = async (): Promise<void> => {
    if (!this.availableOnDevice) {
      return;
    }

    this.emit({
      ...this.state,
      showSetupPrompt: false
    });
  };

  readonly disable = async (): Promise<void> => {
    this.sessionVerified = false;
    this.emit({
      ...this.state,
      isBiometricVerificationEnabled: false,
      isSessionVerified: false
    });
    localStorage.removeItem("biometricEnabled");
  };

  readonly requestEmailLogin = (): void => {
    this.log("requestEmailLogin");
    this.emit({
      ...this.state,
      requestedEmailLogin: true
    });
  };

  readonly clearEmailLoginRequest = (): void => {
    this.emit({
      ...this.state,
      requestedEmailLogin: false
    });
  };

  /**
   * Mark session as verified after successful email/password login fallback.
   * Called when user bypasses biometrics and logs in with email instead.
   */
  readonly markSessionVerifiedAfterEmailLogin = (): void => {
    if (!this.state.requestedEmailLogin) {
      return;
    }

    this.log("markSessionVerifiedAfterEmailLogin");
    this.sessionVerified = true;
    this.emit({
      ...this.state,
      isSessionVerified: true,
      requestedEmailLogin: false
    });
  };
}
